package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.appsyde.shared.app.presentation.team.TeamViewModel
import com.appsyde.shared.presentation.style.style.AppStylesAccount
import com.appsyde.shared.presentation.style.style.AppStylesWizard
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement

import com.appsyde.shared.modules.account.domain.model.AccountRole

/** Bloc "Inviter l'équipe" : en mode wizard = nouveau layout premium (subcards, copy, toast). En mode compte = layout actuel. */
@Composable
fun TeamInviteSection(
    teamViewModel: TeamViewModel,
    teamState: TeamViewModel.State,
    wizardMode: Boolean = false
) {
    var inviteEmail by remember { mutableStateOf("") }
    LaunchedEffect(teamState.createInviteMessage) {
        if (teamState.createInviteMessage != null && !teamState.createInviteError) inviteEmail = ""
    }
         WizardInviteBody(
            teamViewModel = teamViewModel,
            teamState = teamState,
            inviteEmail = inviteEmail,
            setInviteEmail = { inviteEmail = it }
        )

}

@Composable
private fun WizardInviteBody(
    teamViewModel: TeamViewModel,
    teamState: TeamViewModel.State,
    inviteEmail: String,
    setInviteEmail: (String) -> Unit
) {
    // Toast succès : auto-dismiss après 4s
    LaunchedEffect(teamState.createInviteMessage) {
        if (teamState.createInviteMessage != null && !teamState.createInviteError) {
            kotlinx.coroutines.delay(4000)
            teamViewModel.clearCreateInviteMessage()
        }
    }

    // Section 1 : Inviter un membre (placeholder + microcopy, pas de label)
    Div(attrs = { classes(AppStylesWizard.wizardSectionAction) }) {
        Div(attrs = { classes(AppStylesWizard.wizardInputRow) }) {
            Input(InputType.Email, attrs = {
                classes(AppStylesWizard.wizardInput)
                value(inviteEmail)
                onInput { setInviteEmail((it.target as? HTMLInputElement)?.value.orEmpty()) }
                attr("placeholder", "agent@marque.com")
            })
            Button(attrs = {
                classes(AppStylesWizard.wizardBtnSend)
                attr("type", "button")
                if (teamState.createInviteLoading) attr("disabled", "true")
                onClick { teamViewModel.createInvite(inviteEmail) }
            }) { Text(if (teamState.createInviteLoading) "Envoi…" else "Envoyer l'invitation") }
        }
        Div(attrs = { classes(AppStylesWizard.wizardMicrocopy) }) {
            Text("Ils recevront un lien pour rejoindre ce workspace.")
        }
        if (teamState.createInviteMessage != null) {
            Div(attrs = {
                classes(
                    listOfNotNull(
                        AppStylesWizard.wizardToast,
                        if (teamState.createInviteError) AppStylesWizard.wizardToastError else null
                    )
                )
            }) {
                Text(teamState.createInviteMessage!!)
                if (teamState.createInviteError) {
                    Div(attrs = { classes(AppStylesWizard.wizardInputRow) }) {
                        Button(attrs = {
                            classes(AppStylesWizard.wizardBtnSecondary)
                            attr("type", "button")
                            attr("title", "Lien non disponible en cas d'erreur")
                            attr("disabled", "true")
                        }) { Text("Copier le lien") }
                        Button(attrs = {
                            classes(AppStylesWizard.wizardBtnGhostDanger)
                            attr("type", "button")
                            onClick { teamViewModel.clearCreateInviteMessage() }
                        }) { Text("Fermer") }
                    }
                }
            }
        }
    }

    // Section 2 : En attente + Membres (subcards)
    Div(attrs = { classes(AppStylesWizard.wizardSubcard) }) {
        Div(attrs = { classes(AppStylesWizard.wizardSubcardTitle) }) {
            Text("En attente")
            Span(attrs = { classes(AppStylesWizard.wizardBadgeCount) }) { Text("${teamState.invites.size}") }
        }
        if (teamState.isLoading && teamState.invites.isEmpty()) {
            Div(attrs = { classes(AppStylesWizard.wizardRow, AppStylesWizard.wizardRowLast) }) { Text("Chargement…") }
        } else if (teamState.invites.isEmpty()) {
            Div(attrs = { classes(AppStylesWizard.wizardRow, AppStylesWizard.wizardRowLast) }) {
                Span(attrs = { classes(AppStylesWizard.wizardRowEmail) }) { Text("Aucune invitation") }
            }
        } else {
            teamState.invites.forEachIndexed { index, inv ->
                val canRevoke = inv.usedAt == null && inv.revokedAt == null
                val isLast = index == teamState.invites.size - 1
                Div(attrs = {
                    classes(
                        listOfNotNull(
                            AppStylesWizard.wizardRow,
                            if (isLast) AppStylesWizard.wizardRowLast else null
                        ))
                    })
                 {
                    Span(attrs = { classes(AppStylesWizard.wizardRowEmail) }) {
                        Text(inv.email)
                        if (canRevoke) Span(attrs = { classes(AppStylesWizard.wizardBadgeSent) }) { Text("Envoyée") }
                    }
                    if (canRevoke) {
                        Button(attrs = {
                            classes(AppStylesWizard.wizardBtnGhostDanger)
                            attr("type", "button")
                            onClick { teamViewModel.revokeInvite(inv.id) }
                        }) { Text("Révoquer") }
                    }
                }
            }
        }
    }

    Div(attrs = { classes(AppStylesWizard.wizardSubcard) }) {
        Div(attrs = { classes(AppStylesWizard.wizardSubcardTitle) }) {
            Text("Membres")
            Span(attrs = { classes(AppStylesWizard.wizardBadgeCount) }) { Text("${teamState.users.size}") }
        }
        if (teamState.isLoading && teamState.users.isEmpty()) {
            Div(attrs = { classes(AppStylesWizard.wizardRow, AppStylesWizard.wizardRowLast) }) { Text("Chargement…") }
        } else if (teamState.users.isEmpty()) {
            Div(attrs = { classes(AppStylesWizard.wizardRow, AppStylesWizard.wizardRowLast) }) {
                Span(attrs = { classes(AppStylesWizard.wizardRowEmail) }) { Text("Aucun membre") }
            }
        } else {
            teamState.users.forEachIndexed { index, u ->
                val canDisable = u.role != AccountRole.OWNER && u.status == "ACTIVE"
                val isLast = index == teamState.users.size - 1
                Div(attrs = {
                    classes(
                        listOfNotNull(
                            AppStylesWizard.wizardRow,
                            if (isLast) AppStylesWizard.wizardRowLast else null
                        ))
                    })
                 {
                    Span(attrs = { classes(AppStylesWizard.wizardRowEmail) }) { Text(u.email) }
                    Span(attrs = {
                        classes(
                            listOfNotNull(
                                AppStylesWizard.wizardPillRole,
                                if (u.role == AccountRole.OWNER) AppStylesWizard.wizardPillOwner else null
                            )
                        )
                    }) { Text(if (u.role == AccountRole.OWNER) "Owner" else "Member") }
                    if (canDisable) {
                        Button(attrs = {
                            classes(AppStylesWizard.wizardBtnGhostDanger)
                            attr("type", "button")
                            onClick { teamViewModel.disableUser(u.id) }
                        }) { Text("Retirer") }
                    } else {
                        Span() {}
                    }
                }
            }
        }
    }
}

@Composable
private fun AccountInviteBody(
    teamViewModel: TeamViewModel,
    teamState: com.appsyde.shared.app.presentation.team.TeamViewModel.State,
    inviteEmail: String,
    setInviteEmail: (String) -> Unit
) {
    Div(attrs = { classes(AppStylesAccount.configBlock) }) {
        Div(attrs = { classes(AppStylesAccount.sectionTitle) }) { Text("Équipe") }
        Div(attrs = { classes(AppStylesAccount.fieldsGrid) }) {
            Div(attrs = { classes(AppStylesAccount.fieldRow) }) {
                Div(attrs = { classes(AppStylesAccount.fieldLabel) }) { Text("Inviter par email") }
                Div(attrs = { classes(AppStylesAccount.teamInviteRow) }) {
                    Input(InputType.Email, attrs = {
                        classes(AppStylesAccount.teamInviteInput)
                        value(inviteEmail)
                        onInput { setInviteEmail((it.target as? HTMLInputElement)?.value.orEmpty()) }
                        attr("placeholder", "email@exemple.com")
                    })
                    Button(attrs = {
                        classes(AppStylesAccount.teamInviteButton)
                        attr("type", "button")
                        if (teamState.createInviteLoading) attr("disabled", "true")
                        onClick { teamViewModel.createInvite(inviteEmail) }
                    }) { Text(if (teamState.createInviteLoading) "Envoi…" else "Envoyer") }
                }
                if (teamState.createInviteMessage != null) {
                    Div(attrs = {
                        classes(
                            if (teamState.createInviteError) AppStylesAccount.teamMessageError
                            else AppStylesAccount.teamMessageOk
                        )
                    }) {
                        Text(teamState.createInviteMessage)
                        Button(attrs = {
                            classes(AppStylesAccount.actionGhost)
                            attr("type", "button")
                            onClick { teamViewModel.clearCreateInviteMessage() }
                        }) { Text("Fermer") }
                    }
                }
            }
            Div(attrs = { classes(AppStylesAccount.fieldRow) }) {
                Div(attrs = { classes(AppStylesAccount.fieldLabel) }) { Text("Invitations en attente") }
                if (teamState.isLoading && teamState.invites.isEmpty()) {
                    Span(attrs = { classes(AppStylesAccount.fieldValue) }) { Text("Chargement…") }
                } else if (teamState.invites.isEmpty()) {
                    Span(attrs = { classes(AppStylesAccount.fieldValue) }) { Text("Aucune invitation.") }
                } else {
                    teamState.invites.forEach { inv ->
                        val canRevoke = inv.usedAt == null && inv.revokedAt == null
                        Div(attrs = { classes(AppStylesAccount.teamListItem) }) {
                            Span(attrs = { classes(AppStylesAccount.fieldValue) }) { Text("${inv.email}") }
                            if (canRevoke) {
                                Button(attrs = {
                                    classes(AppStylesAccount.actionGhost)
                                    attr("type", "button")
                                    onClick { teamViewModel.revokeInvite(inv.id) }
                                }) { Text("Révoquer") }
                            }
                        }
                    }
                }
            }
            Div(attrs = { classes(AppStylesAccount.fieldRow) }) {
                Div(attrs = { classes(AppStylesAccount.fieldLabel) }) { Text("Membres") }
                if (teamState.isLoading && teamState.users.isEmpty()) {
                    Span(attrs = { classes(AppStylesAccount.fieldValue) }) { Text("Chargement…") }
                } else if (teamState.users.isEmpty()) {
                    Span(attrs = { classes(AppStylesAccount.fieldValue) }) { Text("Aucun membre.") }
                } else {
                    teamState.users.forEach { u ->
                        val canDisable = u.role != AccountRole.OWNER && u.status == "ACTIVE"
                        Div(attrs = { classes(AppStylesAccount.teamListItem) }) {
                            Span(attrs = { classes(AppStylesAccount.fieldValue) }) { Text("${u.email} — ${u.role}") }
                            if (canDisable) {
                                Button(attrs = {
                                    classes(AppStylesAccount.actionDanger)
                                    attr("type", "button")
                                    onClick { teamViewModel.disableUser(u.id) }
                                }) { Text("Retirer") }
                            }
                        }
                    }
                }
            }
        }
    }
}
